import React, { useContext, useEffect, useState } from 'react'
import { MenuItem, Select } from '@mui/material'
import { EmploymentsContext } from '../contexts/EmploymentsContext'
import { useHistory } from 'react-router-dom'
import { changeSelectedStore, SELECTED_STORE_CHANGED_EVENT } from '../Utilities/authenticationService'
import { isMobile } from '../Utilities/isMobile'

const findEmploymentById = (employments, id) =>
    (employments || []).find(employment => String(employment.id) === String(id))

/** Replace an exact path segment only — avoids corrupting longer numeric ids in the URL. */
export const replacePathSegment = (pathname, fromId, toId) => {
    if (fromId == null || fromId === '') return pathname
    const from = String(fromId)
    const to = String(toId)
    const parts = pathname.split('/')
    const idx = parts.findIndex(part => part === from)
    if (idx === -1) return pathname
    parts[idx] = to
    return parts.join('/')
}

const SelectedStore = ({ disabled }) => {
    const selectedEmploymentId = localStorage.getItem('selected_emp')
    const { userEmployments, canEditShiftFor, isManagerFor } = useContext(EmploymentsContext)
    const [selectedStore, setSelectedStore] = useState()
    const history = useHistory()

    // Keep the visible store selection aligned with localStorage.selected_emp.
    // If the stored id is missing from the user's employments, fall back to the
    // first employment and sync localStorage/URL so delete guards stay correct.
    useEffect(() => {
        if (!userEmployments?.length) return

        const matched = findEmploymentById(userEmployments, selectedEmploymentId)
        if (matched) {
            setSelectedStore(matched)
            return
        }

        const fallback = userEmployments[0]
        setSelectedStore(fallback)
        if (fallback && String(fallback.id) !== String(selectedEmploymentId)) {
            changeSelectedStore(fallback.id)
            const nextPath = replacePathSegment(
                history.location.pathname,
                selectedEmploymentId,
                fallback.id
            )
            if (nextPath !== history.location.pathname) {
                history.replace(nextPath)
            }
        }
    }, [userEmployments, selectedEmploymentId, history])

    useEffect(() => {
        const handleStoreChanged = e => {
            const newId = e.detail?.employmentId ?? localStorage.getItem('selected_emp')
            if (!userEmployments?.length) return
            setSelectedStore(findEmploymentById(userEmployments, newId) || userEmployments[0])
        }
        window.addEventListener(SELECTED_STORE_CHANGED_EVENT, handleStoreChanged)
        return () => window.removeEventListener(SELECTED_STORE_CHANGED_EVENT, handleStoreChanged)
    }, [userEmployments])

    const handleChange = e => {
        const newId = e.target.value
        setSelectedStore(findEmploymentById(userEmployments, newId))
        changeSelectedStore(newId)
        if (selectedEmploymentId) {
            const nextPath = replacePathSegment(
                history.location.pathname,
                selectedEmploymentId,
                newId
            )
            if (nextPath !== history.location.pathname) {
                history.replace(nextPath)
            }
        }
    }

    const disableOption = employment => {
        if (!history.location.pathname.includes('/createshift')) return false
        if (canEditShiftFor[employment.id] || isManagerFor[employment.id]) return false
        return true
    }

    if (selectedStore)
        return (
            <div
                style={{
                    margin: '.5rem',
                    background: 'rgba(0, 0, 0, .04)',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: isMobile() ? '60%' : 'auto'
                }}
            >
                <Select
                    disabled={disabled}
                    size="small"
                    sx={{ border: 'none', mx: 'auto' }}
                    fullWidth
                    defaultValue=""
                    value={selectedStore.id} // really doesn't like objects as values
                    onChange={handleChange}
                >
                    {userEmployments.map(employment => (
                        <MenuItem
                            value={employment.id}
                            key={employment.id}
                            disabled={disableOption(employment)}
                            sx={{
                                background: 'rgba(0, 0, 0, .04)'
                            }}
                        >
                            <div style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                {employment?.store?.name}
                            </div>
                        </MenuItem>
                    ))}
                </Select>
            </div>
        )
    return <></>
}

export default SelectedStore
